import type { FormConfig } from '@workwave-tidal/form-fairy'
import type { FormFields, FormErrors } from '../formFields'

import { validateUpdateTenantSource } from '@/features/domain/gps'
import { type AppDispatch } from '@/store'

import { useTexts } from '../useTexts'

export function createGeotabFormValidation(
  tenantSourceId: string,
  dispatch: AppDispatch,
  texts: ReturnType<typeof useTexts>,
): FormConfig<FormFields, FormErrors>['validations'] {
  return [
    {
      id: 'credentialsValidator',
      fields: 'credentials',
      validator: async formApi => {
        const { value: wrongCredentials } = formApi.getField('wrongCredentials')
        const { value: credentials } = formApi.getField('credentials')

        const { value } = formApi.getField('credentials')

        const usernameRequiredError: FormErrors = {
          id: 'usernameRequired',
          field: 'credentials',
          index: 0,
          message: texts.geotab.errors.usernameRequired,
        }

        const passwordRequiredError: FormErrors = {
          id: 'passwordRequired',
          field: 'credentials',
          index: 1,
          message: texts.geotab.errors.passwordRequired,
        }

        const databaseRequiredError: FormErrors = {
          id: 'databaseRequired',
          field: 'credentials',
          index: 2,
          message: texts.geotab.errors.databaseRequired,
        }

        const username = value.find(v => v.fieldName === 'username')
        const password = value.find(v => v.fieldName === 'password')
        const database = value.find(v => v.fieldName === 'database')

        const usernameEmpty = username?.value.trim() === ''
        const passwordEmpty = password?.value.trim() === ''
        const databaseEmpty = database?.value.trim() === ''

        const wrongCredentialsUsernameError: FormErrors = {
          id: 'wrongCredentialsUsernameError',
          field: 'credentials',
          index: 0,
          message: texts.geotab.errors.usernameIncorrect,
        }

        const wrongCredentialsPasswordError: FormErrors = {
          id: 'wrongCredentialsPasswordError',
          field: 'credentials',
          index: 1,
          message: texts.geotab.errors.passwordIncorrect,
        }

        const wrongCredentialsDatabaseError: FormErrors = {
          id: 'wrongCredentialsDatabaseError',
          field: 'credentials',
          index: 2,
          message: texts.geotab.errors.databaseIncorrect,
        }

        if (usernameEmpty || passwordEmpty || databaseEmpty || wrongCredentials) {
          return {
            [usernameRequiredError.id]: usernameEmpty ? usernameRequiredError : null,
            [passwordRequiredError.id]: passwordEmpty ? passwordRequiredError : null,
            [databaseRequiredError.id]: databaseEmpty ? databaseRequiredError : null,
            [wrongCredentialsUsernameError.id]: wrongCredentials
              ? wrongCredentialsUsernameError
              : null,
            [wrongCredentialsPasswordError.id]: wrongCredentials
              ? wrongCredentialsPasswordError
              : null,
            [wrongCredentialsDatabaseError.id]: wrongCredentials
              ? wrongCredentialsDatabaseError
              : null,
            credentialsAlreadyInUse: null,
          }
        }

        const response = await dispatch(
          validateUpdateTenantSource({
            credentials,
            tenantSourceId,
          }),
        )

        if (validateUpdateTenantSource.rejected.match(response)) {
          const errorCode =
            response.payload?.type === 'rpcFailure' ? response.payload.error.errorCode : undefined

          if (errorCode === 101) {
            // An integration to this account already exist
            return {
              [usernameRequiredError.id]: null,
              [passwordRequiredError.id]: null,
              [databaseRequiredError.id]: null,
              [wrongCredentialsUsernameError.id]: null,
              [wrongCredentialsPasswordError.id]: null,
              [wrongCredentialsDatabaseError.id]: null,
              credentialsAlreadyInUse: {
                id: 'credentialsAlreadyInUse',
                field: 'credentials',
                index: 0,
                message: texts.geotab.errors.credentialsAlreadyInUse,
              },
            }
          }

          throw new Error(response.payload?.message ?? 'Internal error')
        }

        return {
          [usernameRequiredError.id]: null,
          [passwordRequiredError.id]: null,
          [databaseRequiredError.id]: null,
          [wrongCredentialsUsernameError.id]: null,
          [wrongCredentialsPasswordError.id]: null,
          [wrongCredentialsDatabaseError.id]: null,
          credentialsAlreadyInUse: null,
        }
      },
    },
  ]
}
